import { AnimatePresence, motion } from "motion/react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PreviewKind } from "@/components/workspace/workspace-file-icons";

interface WorkspaceFilePreviewModalProps {
	open: boolean;
	onClose: () => void;
	loading: boolean;
	error: string | null;
	url: string | null;
	kind: PreviewKind;
}

export function WorkspaceFilePreviewModal({
	open,
	onClose,
	loading,
	error,
	url,
	kind,
}: WorkspaceFilePreviewModalProps) {
	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-scrim/30 backdrop-blur-sm"
					onClick={onClose}
				>
					<motion.div
						initial={{ scale: 0.9, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.9, opacity: 0, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
						className="relative w-full max-w-5xl h-[85vh] bg-bg-surface border border-border-subtle rounded-2xl overflow-hidden shadow-popover flex flex-col"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Modal Header */}
						<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
							<h3 className="text-lg font-medium text-text-primary">
								File Preview
							</h3>
							<Button variant="ghost" size="icon" onClick={onClose}>
								<X size={20} />
							</Button>
						</div>

						{/* Modal Content */}
						<div className="flex-1 min-h-0 relative bg-scrim/30 overflow-hidden">
							{loading ? (
								<div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
									<Loader2 size={40} className="animate-spin text-text-primary" />
									<p className="text-text-tertiary text-sm animate-pulse">
										Preparing preview...
									</p>
								</div>
							) : error ? (
								<div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
									<div className="w-16 h-16 bg-danger-bg rounded-full flex items-center justify-center mb-4">
										<X className="text-danger" size={32} />
									</div>
									<h4 className="text-text-primary text-lg font-medium mb-2">
										Failed to load preview
									</h4>
									<p className="text-text-tertiary text-sm max-w-sm">{error}</p>
									<Button variant="secondary" onClick={onClose} className="mt-6">
										Close
									</Button>
								</div>
							) : url ? (
								// An iframe shows a PDF properly but leaves an image or a
								// video unscaled in the corner, which is what every
								// creative would have looked like.
								kind === "image" ? (
									<img
										src={url}
										alt=""
										className="absolute inset-0 w-full h-full object-contain"
									/>
								) : kind === "video" ? (
									<video
										src={url}
										controls
										className="absolute inset-0 w-full h-full object-contain"
									/>
								) : (
									<iframe
										src={url}
										className="w-full h-full border-none"
										title="File Preview"
									/>
								)
							) : null}
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
